import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  useState,
  useMemo,
  useEffect,
  useDeferredValue,
  useCallback,
} from "react";
import useTitanApp from "@/hooks/useTitanApp";
import { useToast } from "@/hooks/use-toast";
import {
  setIsPatientDashboard,
  setPatientSummary,
  openPatientForm,
  importPatientFromTitan,
  setIsPatientDetailsLoading,
  mergePatientDetails,
  getPatientConsultationCountByPatientId,
  importMultipleFromTitan,
  getPatientsListV2,
  addPatientConsultation,
  setConsultationId,
  setFreeTypeMode,
  setIsFinalSummary,
  setPatientNotes,
  setSignatureDetails,
  setTranscript,
  setConsultationPaused,
  setCurrentConsultationMediaButtonMode,
  setIsConsultationEnabled,
  setPatientTranscription,
  setSelectedPatientConsultation,
  setShowPlayPauseBtn,
  setAiChatSuccessMessages,
  setAddPatientAIChatMessage,
  setPreConsultationTemplate,
  setPreConsultationTranscription,
  setisHandleStartConsultation,
  updateConsultationStatus,
  updateEventStatus,
  setPatientAIChatMessages,
  getPreConsultationByConsultationId,
  setVaccineDetails,
  updateConsultationTemplate,
  getPatientConsultationByConsultationId,
  setIsSelectedPatient,
  setComingFromPatientListView,
  getPatientByPatientId,
  setPatientTabSwitcher,
  SaveAIChatMessage,
  setSnomedCodes,
} from "@/dispatchers/patientDispatchers";

import {
  setisPatientSearched,
  setPatientInfoTab,
  setRoutedFromScheduler,
} from "@/dispatchers/appDispatchers";
import {
  setIsTemplateSelected,
  setSelectedTemplate,
  setSelectedTemplateContent,
  setStreamedText,
  sendAIQAService,
} from "@/dispatchers/templateDispatchers";
import { ConsultationButtonStatus } from "@/enum/ConsultationButtonStatus";
import {
  showError,
  showSuccess,
} from "@/components/ToastedMessage/ToastedMessage";
import { useNHS111Data } from "../nhs111/nhs111Data";
import { notificationsHub } from "@/services/notificationsHub";
import { Loader2 } from "lucide-react";
import { getCompanyDocumentsByCompanyId } from "@/dispatchers/companyDocumentsDispatchers";
import { extractSnomedCodesFromSummary } from "@/utils/extractSnomedCodesFromSummary";
import { setShowNHS111ReferralTab } from "@/dispatchers/pharmacyServiceDispatchers";

interface Tag {
  colour: string;
  tagName: string;
}
export interface Patient {
  id: string;
  name: string;
  age: number;
  imported: boolean;
  address: string;
  tags: Tag[];
  lastConsultation: string;
  isConsultationActive?: string;
  consultationId?: string;
  practitioner: string;
  isPdsVerified?: boolean;
  isPDSEntry?: boolean;
  originalPatient: any;
  pharmacyName?: string;
  pharmacyId?: string;
  dob?: string;
}

export const usePatientListviewData = () => {
  const { appState, patientState } = useTitanApp();
  const { darkMode, currentPharmacyId, companyId } = appState;
  const {
    patientListLoader,
    mappedPatients,
    patientForm,
    mergePatientDetailsLoader,
    patientConsultationCountLoading,
    isImportMultipleFromTitanLoading,
    preConsultData,
    consultationData,
    comingFromPatientListView,
    patientAIChatMessages,
  } = patientState || {};
  const [loadingRowId, setLoadingRowId] = useState(null);
  const navigate = useNavigate();
  const routerNavigate = useNavigate();

  const location = useLocation();
  const navigateFromDashboard =
    location.state && location?.state?.moveFromDashboard;
  const selectedPatientName =
    location.state && location?.state?.selectedPatientName;
  const selectedPatientAddress =
    location.state && location?.state?.selectedPatientAddress;
  const pharmacyAvgVal = location.state && location?.state?.pharmacyAvgVal;

  const [selectedPatients, setSelectedPatients] = useState(new Set());
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mergePair, setMergePair] = useState(null);
  const [patientsFilter, setPatientsFilter] = useState("");
  const [assigneeFilter, setAssigneeFilter] = useState("all");
  const [consultationStatusFilter, setConsultationStatusFilter] = useState(
    "all",
  );
  const [showDuplicatesOnly, setShowDuplicatesOnly] = useState(false);
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const [treatmentPlanFilter, setTreatmentPlanFilter] = useState("all");
  const [includeTitanPatients, setIncludeTitanPatients] = useState(false);
  const [searchTitanEnabled, setSearchTitanEnabled] = useState(false);
  const [triggerTitanSearch, setTriggerTitanSearch] = useState(false);
  const { setNHS111ConsultationSummary } = useNHS111Data();

  const { pharmacyId } = useParams() || {
    pharmacyId: "00000000-0000-0000-0000-000000000000",
  };

  const handleSearchTitanEnabledChange = (value: boolean) => {
    setSearchTitanEnabled(value);
    setIncludeTitanPatients(value);
    setTriggerTitanSearch(true);
  };

  useEffect(() => {
    if (!triggerTitanSearch) return;

    const fetchTitanPatients = async () => {
      try {
        const payload = {
          keyword: patientsFilter || "",
          practitioners: "",
          treatmentPlan: "",
          isTitanSearch: searchTitanEnabled,
          consultationStatus: 0,
          pharmacyID: pharmacyId,
          companyId: companyId,
        };

        await getPatientsListV2(payload);
      } finally {
        setTriggerTitanSearch(false);
      }
    };

    fetchTitanPatients();
  }, [triggerTitanSearch, pharmacyId]);

  // Reset all filters when search field is cleared
  useEffect(() => {
    if (patientsFilter.trim() !== "") return;

    setAssigneeFilter("all");
    setConsultationStatusFilter("all");
    setTreatmentPlanFilter("all");
    setShowDuplicatesOnly(false);
    setIncludeTitanPatients(false);
    setSearchTitanEnabled(false);
  }, [patientsFilter]);

  const handleSearch = async (value) => {
    if (value.length === 0) {
      const payload = {
        keyword: "",
        practitioners: "",
        treatmentPlan: "",
        isTitanSearch: false,
        consultationStatus: 0,
        pharmacyID: pharmacyId,
        companyId: companyId,
      };

      await getPatientsListV2(payload);
    }
  };

  const { toast } = useToast();

  const deferredPatientsFilter = useDeferredValue(patientsFilter);

  const assignees = useMemo(
    () => [
      "all",
      ...Array.from(new Set(mappedPatients.map((p) => p.practitioner))),
    ],
    [mappedPatients],
  );

  const extractUkPostcode = (address: any) => {
    if (!address) return "";

    const ukPostcodeRegex = /\b([A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2})\b/i;

    const match = String(address).match(ukPostcodeRegex);

    return match ? match[1].toUpperCase().replace(/\s+/g, "") : "";
  };

  const duplicatePatientIds = useMemo(() => {
    const normalizeNhs = (nhs: any) => {
      if (nhs === undefined || nhs === null) return "";
      const s = String(nhs)
        .toUpperCase()
        .replace(/\s+/g, "")
        .replace(/\./g, "");
      if (
        s === "" ||
        s === "NA" ||
        s === "N/A" ||
        s === "N.A" ||
        s === "N.A."
      ) {
        return "";
      }
      return s;
    };

    const duplicateIds = new Set<string>();
    const nhsMap = new Map<string, string[]>();
    const dobNameMap = new Map<string, string[]>();
    const dobPostcodeMap = new Map<string, string[]>();
    const namePostcodeMap = new Map<string, string[]>();

    const addToMap = (map: Map<string, string[]>, key: string, id: string) => {
      const existing = map.get(key);
      if (existing) existing.push(id);
      else map.set(key, [id]);
    };

    for (const p of mappedPatients) {
      const nhs = normalizeNhs(p.nhs);
      const dob = p.dob || "";
      const name = p.name.toLowerCase().trim();
      const postcode = extractUkPostcode(p.address);

      if (nhs) addToMap(nhsMap, nhs, p.id);
      if (dob && name) addToMap(dobNameMap, `${dob}||${name}`, p.id);
      if (dob && postcode)
        addToMap(dobPostcodeMap, `${dob}||${postcode}`, p.id);
      if (name && postcode)
        addToMap(namePostcodeMap, `${name}||${postcode}`, p.id);
    }

    for (const [, ids] of nhsMap) {
      if (ids.length > 1) ids.forEach((id) => duplicateIds.add(id));
    }
    for (const [, ids] of dobNameMap) {
      if (ids.length > 1) ids.forEach((id) => duplicateIds.add(id));
    }
    for (const [, ids] of dobPostcodeMap) {
      if (ids.length > 1) ids.forEach((id) => duplicateIds.add(id));
    }
    for (const [, ids] of namePostcodeMap) {
      if (ids.length > 1) ids.forEach((id) => duplicateIds.add(id));
    }

    return duplicateIds;
  }, [mappedPatients]);

  const filteredPatients = useMemo(() => {
    const searchLower = deferredPatientsFilter?.toLowerCase() || "";
    const searchWords = searchLower.split(/\s+/).filter(Boolean);

    const filtered = mappedPatients.filter((p) => {
      // --- 0. TITAN TAG FILTER (NEW - does NOT remove your old logic) ---
      const hasTitanTag = p.tags?.some(
        (tag) =>
          (typeof tag === "string" ? tag : tag?.tagName || "").toLowerCase() ===
          "titan",
      );

      if (hasTitanTag && !searchTitanEnabled && !includeTitanPatients) {
        return false;
      }

      // --- 1. SPECIAL CASE: "titan" SEARCH (UNCHANGED) ---
      if (searchLower === "titan") {
        return !p.imported;
      }

      // --- 2. SEARCH FILTER ---
      let matchesSearch = true;

      if (searchLower) {
        const nameFields = [
          p.name,
          p.originalPatient?.foreName,
          p.originalPatient?.middleName,
          p.originalPatient?.surName,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        const nameMatch = searchWords.every((word) =>
          nameFields.includes(word),
        );

        const addressMatch = p.address?.toLowerCase().includes(searchLower);

        const tagsMatch = p.tags?.some((tag) =>
          (typeof tag === "string" ? tag : tag?.tagName || "")
            .toLowerCase()
            .includes(searchLower),
        );

        const nhsMatch = p.nhs
          ?.replace(/\s+/g, "")
          .includes(searchLower.replace(/\s+/g, ""));

        const ageMatch =
          p.age !== undefined &&
          p.age !== null &&
          p.age.toString().includes(searchLower);

        const dobMatch = p.dob?.toLowerCase().includes(searchLower);

        matchesSearch =
          nameMatch ||
          addressMatch ||
          tagsMatch ||
          nhsMatch ||
          ageMatch ||
          dobMatch;
      }

      // --- 3. ASSIGNEE FILTER ---
      const matchesAssignee =
        assigneeFilter === "all" || p.practitioner === assigneeFilter;

      // --- 4. CONSULTATION STATUS FILTER ---
      const matchesConsultationStatus =
        consultationStatusFilter === "all" ||
        (consultationStatusFilter === "active" &&
          p.isConsultationActive === "Active") ||
        (consultationStatusFilter === "inactive" &&
          p.isConsultationActive !== "Active");

      // --- 5. DUPLICATE PATIENT FILTER ---
      const matchesDuplicateFilter =
        !showDuplicatesOnly || duplicatePatientIds.has(p.id);

      // --- 6. TREATMENT PLAN FILTER (NEW) ---
      const treatmentPlans = (p as any).treatmentPlans;

      const matchesTreatmentPlan =
        !treatmentPlanFilter ||
        treatmentPlanFilter === "all" ||
        (treatmentPlanFilter === "with-plan" &&
          Array.isArray(treatmentPlans) &&
          treatmentPlans.length > 0) ||
        (treatmentPlanFilter === "without-plan" &&
          (!Array.isArray(treatmentPlans) || treatmentPlans.length === 0)) ||
        (treatmentPlanFilter === "active-plan" &&
          Array.isArray(treatmentPlans) &&
          treatmentPlans.some((tp: any) => tp.status === "active"));

      return (
        matchesSearch &&
        matchesAssignee &&
        matchesConsultationStatus &&
        matchesTreatmentPlan &&
        matchesDuplicateFilter
      );
    });

    // --- 7. SORTING (UNCHANGED) ---
    return filtered.sort((a, b) => {
      if (a.imported !== b.imported) {
        return a.imported ? -1 : 1;
      }

      if (sortField === "lastConsultation") {
        const aTime = a.lastConsultation
          ? Date.parse(a.lastConsultation)
          : null;

        const bTime = b.lastConsultation
          ? Date.parse(b.lastConsultation)
          : null;

        // Patients with no date go to the bottom
        if (aTime === null && bTime === null) return 0;
        if (aTime === null) return 1;
        if (bTime === null) return -1;

        return sortDirection === "asc" ? aTime - bTime : bTime - aTime;
      }

      let aValue = a[sortField];
      let bValue = b[sortField];

      if (sortField === "age") {
        aValue = Number(aValue);
        bValue = Number(bValue);
      } else if (
        ["name", "address", "assignee", "practitioner"].includes(sortField)
      ) {
        aValue = aValue?.toLowerCase() || "";
        bValue = bValue?.toLowerCase() || "";
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [
    mappedPatients,
    deferredPatientsFilter,
    assigneeFilter,
    consultationStatusFilter,
    treatmentPlanFilter,
    showDuplicatesOnly,
    duplicatePatientIds,
    sortField,
    sortDirection,
    includeTitanPatients,
    searchTitanEnabled,
  ]);

  const selectAll = useMemo(() => {
    if (filteredPatients.length === 0) return false;
    return filteredPatients.every((p) => selectedPatients.has(p.id));
  }, [filteredPatients, selectedPatients]);

  const handleAddPatient = () => {
    openPatientForm(true);
  };

  const handleSelectAll = (checked) => {
    setSelectedPatients((prev) => {
      const next = new Set(prev);
      if (checked) {
        filteredPatients.forEach((p) => next.add(p.id));
      } else {
        filteredPatients.forEach((p) => next.delete(p.id));
      }
      return next;
    });
  };

  const handleSelectPatient = (patientId, checked) => {
    setSelectedPatients((prev) => {
      const next = new Set(prev);
      if (checked) next.add(patientId);
      else next.delete(patientId);
      return next;
    });
  };

  const handleClearSelection = () => setSelectedPatients(new Set());

  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // Set new field with ascending direction
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const handleMerge = async () => {
    setShowMergeModal(true);

    const count = selectedPatients.size;
    if (count < 2) {
      toast({
        title: "Select at least two patients",
        description: "Please select two or more patients to merge.",
      });
      return;
    }
    if (count !== 2) return;
    const ids = Array.from(selectedPatients);

    const a = mappedPatients.find((p) => p.id === ids[0]);
    const b = mappedPatients.find((p) => p.id === ids[1]);

    if (!a || !b) return;

    try {
      const payload = {
        patientIds: [a.id.toString(), b.id.toString()],
      };

      const response = await getPatientConsultationCountByPatientId(payload);

      const countsMap = response?.consultationCountByPatientId || {};

      const updatedA = {
        ...a,
        consultationsCount: countsMap[a.id] ?? 0,
      };

      const updatedB = {
        ...b,
        consultationsCount: countsMap[b.id] ?? 0,
      };

      setMergePair([updatedA, updatedB]);
    } catch (error) {
      toast({
        title: "Unable to prepare merge",
        description: "Something went wrong while loading patient data.",
        variant: "destructive",
      });
    }
  };

  const onPatientClick = async (params) => {
    setIsPatientDetailsLoading(true);
    const patient = params.originalPatient;
    // Don't route if patient is not imported (TITAN patient)
    if (patient.imported === false) {
      setIsPatientDetailsLoading(false);
      return;
    }
    setisPatientSearched(true);
    setPatientInfoTab(0);
    setIsPatientDashboard(true);
    setIsTemplateSelected(false);
    setSelectedTemplate([]);
    setPatientSummary("");
    setSelectedTemplateContent([]);
    setPatientAIChatMessages([]);
    setConsultationPaused(false);
    setShowPlayPauseBtn(false);
    setRoutedFromScheduler(false);
    navigate(`patientdashboard-v2/${currentPharmacyId}/${patient?.patientID}`, {
      replace: true,
      state: {
        onDarkModeChange: darkMode,
        moveFromDashboard: navigateFromDashboard,
        selectedPatientName: selectedPatientName,
        selectedPatientAddress: selectedPatientAddress,
        pharmacyAvgVal: pharmacyAvgVal,
        latitude: 54.7023545,
        logitude: -3.2765753,
      },
    });
  };

  const onConsultationClick = async (patient, consultationId) => {
    setComingFromPatientListView(true);
    try {
      await getPatientByPatientId(patient?.id);
      var consultation = await getPatientConsultationByConsultationId(
        consultationId,
      );
      setNHS111ConsultationSummary(consultation);

      if (consultation?.preConsultationTemplate) {
        await getPreConsultationByConsultationId({
          consultationId: consultationId,
        });
      }
      if (consultation?.consultationChat) {
        const parsedAiChatMessages = JSON.parse(consultation.consultationChat);
        try {
          parsedAiChatMessages.forEach((message) => {
            setAddPatientAIChatMessage(message);
          });
        } catch (err) {
          console.error("Invalid consultationChat JSON:", err);
        }
        setPatientAIChatMessages(parsedAiChatMessages);
      }
      setIsSelectedPatient(patient?.name);
      setConsultationId(consultationId);
      setisPatientSearched(true);
      setFreeTypeMode(false);
      setIsConsultationEnabled(false);
      setisHandleStartConsultation(false);

      setPatientInfoTab(1);
      setShowNHS111ReferralTab(false);

      setPatientTabSwitcher(consultation?.preConsultationTemplate ? 1 : 0);

      navigate(
        `/pharmacy/patientconsultation/${currentPharmacyId}/${patient?.id}/${consultationId}`,
      );
    } catch (error) {
      console.error("Failed to handle consultation click:", error);
      showError("Unable to load consultation. Please try again.", true, 5000);
    }
  };

  useEffect(() => {
    if (!comingFromPatientListView) return;
    if (!consultationData) return;

    setIsConsultationEnabled(false);
    setSelectedPatientConsultation(consultationData);
    setConsultationId(consultationData.consultationId);

    setPatientTranscription(consultationData.transcription);
    setTranscript(consultationData.transcription);
    setSelectedTemplate(consultationData.template);
    setSignatureDetails(consultationData.patientConsent);
    setIsFinalSummary(!!consultationData.notes);
    setPatientNotes(consultationData.notes ?? "");
    setShowPlayPauseBtn(consultationData.consultationStatus === 3);
    const { mainSummary, snomedCodes } = extractSnomedCodesFromSummary(
      consultationData?.consultationSummary,
    );

    setStreamedText(mainSummary);
    setSnomedCodes(snomedCodes);

    if (consultationData?.template?.linkedDocumentId && companyId) {
      getCompanyDocumentsByCompanyId(companyId);
    }

    setConsultationPaused(
      consultationData.consultationStatus === 3 &&
        !!consultationData.transcription?.trim(),
    );

    if (
      consultationData.template &&
      [2, 3].includes(consultationData.consultationStatus)
    ) {
      setIsTemplateSelected(true);
    }

    if (
      consultationData.consultationButtonStatus ===
      ConsultationButtonStatus.Manual
    ) {
      setCurrentConsultationMediaButtonMode("manual");
    }
  }, [consultationData, comingFromPatientListView]);

  useEffect(() => {
    if (!comingFromPatientListView) return;
    if (!preConsultData?.preConsultationTemplate) {
      setPreConsultationTranscription("");
      return;
    }
    setPreConsultationTemplate(preConsultData.preConsultationTemplate);

    let text = "";
    preConsultData.preConsultationTemplate.templateSections?.forEach(
      (section) => {
        section.userControls?.forEach((control) => {
          if (control.label && control.value) {
            text += ` ${control.label} ${control.value} `;
          }
        });
      },
    );

    setPreConsultationTranscription(text.trim());
  }, [preConsultData, comingFromPatientListView]);

  useEffect(() => {
    if (!comingFromPatientListView) return;
    if (!consultationData?.template) return;
    if (!consultationData?.transcription?.trim()) return;

    const filledCount = countFilledControls(consultationData.template);
    if (filledCount > 0) return;

    (async () => {
      const allControls = consultationData.template.templateSections.flatMap(
        (sec) => sec.userControls,
      );

      const questions = allControls.map((ctrl, idx) => ({
        [`q${idx + 1}`]: ctrl.label,
      }));

      const qaResponse = await sendAIQAService({
        q: questions,
        t: consultationData.transcription,
      });

      const updatedTemplate = fillTemplateWithQA(
        consultationData.template,
        qaResponse,
      );

      setSelectedTemplate(updatedTemplate);

      await updateConsultationTemplate({
        consultationId: consultationData.consultationId,
        payload: updatedTemplate.templateSections,
      });
    })();
  }, [consultationData, comingFromPatientListView]);

  useEffect(() => {
    return () => setComingFromPatientListView(false);
  }, []);

  function countFilledControls(template) {
    let count = 0;
    template?.templateSections?.forEach((section) => {
      section.userControls.forEach((ctrl) => {
        if (ctrl.value && ctrl.value?.trim() !== "") count++;
      });
    });
    return count;
  }

  function fillTemplateWithQA(template, qaResponse) {
    let answers = {};
    try {
      answers = JSON.parse(qaResponse?.response || "{}");
    } catch (e) {
      console.error("Invalid QA response JSON", e);
    }

    let counter = 1;
    const updatedSections = template?.templateSections?.map((section) => ({
      ...section,
      userControls: section?.userControls?.map((ctrl) => {
        const key = `a${counter}`;
        const newValue =
          answers.hasOwnProperty(key) && answers[key] !== null
            ? answers[key]
            : ctrl.value;
        counter++;
        return { ...ctrl, value: newValue };
      }),
    }));

    return {
      ...template,
      templateSections: updatedSections,
    };
  }

  const handleMergePatientsClick = async (merged) => {
    const fullName = merged.name;
    const nameParts = fullName.trim().split(/\s+/);
    const forename = nameParts[0] || "";
    const surname = nameParts.length > 1 ? nameParts[nameParts.length - 1] : "";
    const middleName =
      nameParts.length > 2 ? nameParts.slice(1, -1).join(" ") : "";

    const dob = merged.dob;
    const [day, month, year] = dob.split("/");
    const isoDob = new Date(`${year}-${month}-${day}T00:00:00Z`).toISOString();

    const finalPayload = {
      patientID: merged.patientID,
      otherPatientID: merged.otherPatientID,
      foreName: forename,
      middleName: middleName,
      surName: surname,
      dob: isoDob,
      nhsNumber: merged.nhs,
      address: merged.address,
      phone: merged.contact,
      email: merged.email,
      doctor: merged.assignees,
      mergeTags: merged.mergeTags,
      mergeAllergies: merged.mergeAllergies,
    };

    try {
      const unsubscribe = await notificationsHub.subscribeMergeResult(
        merged.patientID,
        async (payload) => {
          const data = {
            ...payload,
            json: payload.json ? JSON.parse(payload.json) : null,
          };
          toast({
            title: "Merge successful ✅",
            description:
              payload?.message || "Patients have been successfully merged.",
            duration: 8000,
          });
          unsubscribe();
          const payloadPatient = {
            keyword: "",
            practitioners: "",
            treatmentPlan: "",
            isTitanSearch: false,
            consultationStatus: 0,
            pharmacyID: currentPharmacyId,
            companyId: companyId,
          };
          await getPatientsListV2(payloadPatient);
        },
        (errorMessage) => {
          showError(
            errorMessage || "Merge failed. Please contact support.",
            true,
            6000,
          );
          unsubscribe();
        },
      );
      const response = await mergePatientDetails(finalPayload);

      if (!response) {
        showError(
          "Failed to merge patients. Please contact support.",
          true,
          5000,
        );
        return;
      }

      setShowWarningDialog(false);
      setShowMergeModal(false);
      setMergePair(null);
      setSelectedPatients(new Set());

      toast({
        title: (
          <span className="flex items-center gap-2">
            Merge in progress
            <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
          </span>
        ) as any,
        description: `We're merging ${mergePair[0].name} and ${mergePair[1].name}. We'll notify you once it's done.`,
        duration: 8000,
      });
    } catch (error) {
      console.error("Failed to merge patients:", error);
      showError(
        "Unable to merge patients. Please check your internet connection and try again.",
        true,
        5000,
      );
    }
  };

  const onImportFromTitan = async () => {
    const titanPatients = Array.from(selectedPatients)
      .map((id) => filteredPatients.find((p) => p.id === id))
      .filter((p) => p && p.imported === false);

    const patientIds = titanPatients.map((p) => p.id);

    try {
      await importMultipleFromTitan({
        patientIDs: patientIds,
        pharmacyID: currentPharmacyId,
      });

      const payload = {
        keyword: "",
        practitioners: "",
        treatmentPlan: "",
        isTitanSearch: false,
        consultationStatus: 0,
        pharmacyID: currentPharmacyId,
        companyId: companyId,
      };

      await getPatientsListV2(payload);

      showSuccess(
        `Successfully imported ${titanPatients.length} TITAN patient${
          selectedPatients.size !== 1 ? "s" : ""
        }.`,
      );
      setSelectedPatients(new Set());
      setPatientsFilter("");
      setAssigneeFilter("all");
      setConsultationStatusFilter("all");
      setTreatmentPlanFilter("all");
      setShowDuplicatesOnly(false);
      setIncludeTitanPatients(false);
      setSearchTitanEnabled(false);
    } catch (error) {
      console.error("Error importing patients:", error);
      showError("Failed to import patients. Please try again.", true, 5000);
    }
  };

  return {
    /* =======================
     States / Values
  ======================= */
    darkMode,
    patientListLoader,
    patientsFilter,
    assigneeFilter,
    consultationStatusFilter,
    showDuplicatesOnly,
    duplicatePatientIds,
    selectedPatients,
    sortField,
    sortDirection,
    mergePair,
    showMergeModal,
    patientForm,
    loadingRowId,
    mergePatientDetailsLoader,
    patientConsultationCountLoading,
    showWarningDialog,
    treatmentPlanFilter,
    searchTitanEnabled,
    includeTitanPatients,
    currentPharmacyId,
    /* =======================
     Derived / Computed Data
  ======================= */
    assignees,
    filteredPatients,
    selectAll,

    /* =======================
     Setters
  ======================= */
    setPatientsFilter,
    setAssigneeFilter,
    setConsultationStatusFilter,
    setShowDuplicatesOnly,
    setShowMergeModal,
    setShowWarningDialog,
    setTreatmentPlanFilter,
    setSearchTitanEnabled,
    setIncludeTitanPatients,
    setTriggerTitanSearch,

    /* =======================
     Handlers / Actions
  ======================= */
    onPatientClick,
    handleAddPatient,
    handleSelectAll,
    handleSelectPatient,
    handleSort,
    handleMerge,
    handleClearSelection,
    handleMergePatientsClick,
    openPatientForm,
    onImportFromTitan,
    isImportMultipleFromTitanLoading,
    handleSearchTitanEnabledChange,
    onConsultationClick,
    handleSearch,
  };
};
